import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Calculator, Clock, FileCheck } from 'lucide-react';
import { toast } from 'sonner';

// Fee Calculator Component
export function FeeCalculator({ services, onCalculate }) {
  const [dept, setDept] = useState('');
  const [service, setService] = useState('');
  const [nationality, setNationality] = useState('default');
  const [urgent, setUrgent] = useState(false);

  const availableServices = dept && services[dept] ? services[dept].services : [];

  const handleCalculate = () => {
    if (!service) {
      toast.error('Please select a service');
      return;
    }
    onCalculate(service, nationality, urgent);
  };

  return (
    <div className="space-y-4">
      <Select value={dept} onValueChange={(v) => { setDept(v); setService(''); }}>
        <SelectTrigger>
          <SelectValue placeholder="Select department" />
        </SelectTrigger>
        <SelectContent>
          {Object.entries(services).map(([code, data]) => (
            <SelectItem key={code} value={code}>{data.name}</SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Select value={service} onValueChange={setService} disabled={!dept}>
        <SelectTrigger>
          <SelectValue placeholder="Select service" />
        </SelectTrigger>
        <SelectContent>
          {availableServices.map((svc) => (
            <SelectItem key={svc.code} value={svc.code}>{svc.name}</SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Select value={nationality} onValueChange={setNationality}>
        <SelectTrigger>
          <SelectValue placeholder="Nationality" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="default">Standard</SelectItem>
          <SelectItem value="gcc">GCC National</SelectItem>
          <SelectItem value="india">India</SelectItem>
          <SelectItem value="philippines">Philippines</SelectItem>
          <SelectItem value="uk">UK</SelectItem>
          <SelectItem value="usa">USA</SelectItem>
          <SelectItem value="europe">Europe</SelectItem>
        </SelectContent>
      </Select>

      <div className="flex items-center gap-2">
        <Switch checked={urgent} onCheckedChange={setUrgent} />
        <Label>Urgent Processing (+50%)</Label>
      </div>

      <Button className="w-full" onClick={handleCalculate} disabled={!service}>
        <Calculator className="w-4 h-4 mr-2" />
        Calculate Fee
      </Button>
    </div>
  );
}

// Timeline Estimator Component
export function TimelineEstimator({ services, onEstimate }) {
  const [dept, setDept] = useState('');
  const [service, setService] = useState('');
  const [urgent, setUrgent] = useState(false);

  const availableServices = dept && services[dept] ? services[dept].services : [];

  return (
    <div className="space-y-4">
      <Select value={dept} onValueChange={(v) => { setDept(v); setService(''); }}>
        <SelectTrigger>
          <SelectValue placeholder="Select department" />
        </SelectTrigger>
        <SelectContent>
          {Object.entries(services).map(([code, data]) => (
            <SelectItem key={code} value={code}>{data.name}</SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Select value={service} onValueChange={setService} disabled={!dept}>
        <SelectTrigger>
          <SelectValue placeholder="Select service" />
        </SelectTrigger>
        <SelectContent>
          {availableServices.map((svc) => (
            <SelectItem key={svc.code} value={svc.code}>{svc.name}</SelectItem>
          ))}
        </SelectContent>
      </Select>

      <div className="flex items-center gap-2">
        <Switch checked={urgent} onCheckedChange={setUrgent} />
        <Label>Urgent Processing</Label>
      </div>

      <Button className="w-full" onClick={() => onEstimate(service, urgent)} disabled={!service}>
        <Clock className="w-4 h-4 mr-2" />
        Get Timeline
      </Button>
    </div>
  );
}

// Document Checker Component
export function DocumentChecker({ services, onCheck }) {
  const [dept, setDept] = useState('');
  const [service, setService] = useState('');

  const availableServices = dept && services[dept] ? services[dept].services : [];

  return (
    <div className="space-y-4">
      <Select value={dept} onValueChange={(v) => { setDept(v); setService(''); }}>
        <SelectTrigger>
          <SelectValue placeholder="Select department" />
        </SelectTrigger>
        <SelectContent>
          {Object.entries(services).map(([code, data]) => (
            <SelectItem key={code} value={code}>{data.name}</SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Select value={service} onValueChange={setService} disabled={!dept}>
        <SelectTrigger>
          <SelectValue placeholder="Select service" />
        </SelectTrigger>
        <SelectContent>
          {availableServices.map((svc) => (
            <SelectItem key={svc.code} value={svc.code}>{svc.name}</SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Button className="w-full" onClick={() => onCheck(service)} disabled={!service}>
        <FileCheck className="w-4 h-4 mr-2" />
        Get Checklist
      </Button>
    </div>
  );
}
